import React, { useEffect, useState } from 'react';
import { DataProviderInstance } from '../../api/DataProvider';
import styles from './AppointmentTypes.module.css';
import {
    Skeleton,
    Switch,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    ViewModuleOutlined as AppointmentTypesIcon
} from '@mui/icons-material/';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import CustomButton from '../../components/buttons/CustomButton';
import CreateEditAppointmentType from '../../popup/CreateEditAppointmentType/CreateEditAppointmentType';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import Toast from '../../components/support/Toast';
import AppointmentTypeCard from './components/AppointmentTypeCard';
import AppointmentTypesEmptyState, { ADD_DENIED } from './components/AppointmentTypesEmptyState';
import HowItWorks from './components/HowItWorks';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { USER_ROLES } from '../../constants/userRoles';

const SKELETON_CARDS = 3;

/**
 * Cadastros > Tipos de atendimento: tipos de atendimento da empresa
 * (atendimentos modulares). A lista é CompanyConfig.AppointmentTypes, que o
 * DataProvider recarrega após cada gravação.
 */
const AppointmentTypes = () => {
    const {
        CompanyConfig, GetAppointmentTypes, UpdateAppointmentType, DeactivateAppointmentType,
        GetInactiveAppointmentTypes, ReactivateAppointmentType,
    } = DataProviderInstance();
    const types = CompanyConfig?.AppointmentTypes || [];
    // Criar, editar, definir padrão e desativar são só de proprietário e administrador (RoleAccessMap)
    const { hasRole } = useRoleAccess();
    const canManage = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);

    const [currentItem, setCurrentItem] = useState(null);
    const [isVisibleEditor, setIsVisibleEditor] = useState(false);
    const [isVisibleConfirmPopup, setIsVisibleConfirmPopup] = useState(false);
    const [isVisibleReactivatePopup, setIsVisibleReactivatePopup] = useState(false);
    // "Exibir inativos": os desativados vêm à parte (CompanyConfig guarda só os ativos)
    const [showInactive, setShowInactive] = useState(false);
    const [inactiveTypes, setInactiveTypes] = useState([]);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    // Primeira leitura: sem ela a lista vazia apareceria antes de os tipos chegarem
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        GetAppointmentTypes(() => setLoaded(true));
    }, []);

    const loadInactive = () => GetInactiveAppointmentTypes(setInactiveTypes);

    useEffect(() => {
        if (showInactive) loadInactive();
    }, [showInactive]);

    const notify = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const handleAddType = () => {
        if (!canManage) return;
        setCurrentItem(null);
        setIsVisibleEditor(true);
    };

    const handleEditType = (type) => {
        if (!canManage) return;
        setCurrentItem(type);
        setIsVisibleEditor(true);
    };

    const handleSetDefault = (type) => {
        if (!canManage) return;
        UpdateAppointmentType({
            id: type.Id,
            label: type.Label,
            modules: type.Modules,
            config: type.Config,
            isDefault: true
        }, (response) => {
            if (response?.status === 'SUCCESS') {
                notify(`"${type.Label}" agora é o tipo padrão`);
            } else {
                notify(response?.error || 'Não foi possível alterar o tipo padrão.', 'error');
            }
        });
    };

    const handleDeactivateType = (type) => {
        if (!canManage) return;
        setCurrentItem(type);
        setIsVisibleConfirmPopup(true);
    };

    const handleConfirmDeactivate = () => {
        if (!currentItem) return;
        DeactivateAppointmentType(currentItem.Id, (response) => {
            if (response?.status === 'SUCCESS') {
                notify('Tipo de atendimento desativado');
                if (showInactive) loadInactive();
            } else {
                notify(response?.error || 'Não foi possível desativar o tipo.', 'error');
            }
        });
    };

    const handleReactivateType = (type) => {
        if (!canManage) return;
        setCurrentItem(type);
        setIsVisibleReactivatePopup(true);
    };

    const handleConfirmReactivate = () => {
        if (!currentItem) return;
        ReactivateAppointmentType(currentItem.Id, (response) => {
            if (response?.status === 'SUCCESS') {
                notify('Tipo de atendimento reativado');
                loadInactive();
            } else {
                notify(response?.error || 'Não foi possível reativar o tipo.', 'error');
            }
        });
    };

    const handleReactivatePopupHide = () => {
        setIsVisibleReactivatePopup(false);
        setCurrentItem(null);
    };

    const handleConfirmPopupHide = () => {
        setIsVisibleConfirmPopup(false);
        setCurrentItem(null);
    };

    const handleEditorClose = () => {
        setIsVisibleEditor(false);
        setCurrentItem(null);
    };

    const handleSaved = () => {
        handleEditorClose();
        notify('Tipo de atendimento salvo');
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    return (
        <div className={styles.container}>
            <Toast
                open={notification.open}
                onClose={handleCloseNotification}
                message={notification.message}
                severity={notification.severity}
                autoHideDuration={6000}
            />

            {isVisibleConfirmPopup && currentItem && (
                <ConfirmPopup
                    Visibility={isVisibleConfirmPopup}
                    Title="Desativar tipo de atendimento"
                    Description={`Deseja desativar o tipo "${currentItem.Label}"? Os atendimentos já registrados com ele continuam iguais; o tipo só deixa de aparecer na escolha.`}
                    ConfirmLabel="Desativar"
                    OnConfirm={handleConfirmDeactivate}
                    OnHide={handleConfirmPopupHide}
                />
            )}
            {isVisibleReactivatePopup && currentItem && (
                <ConfirmPopup
                    Visibility={isVisibleReactivatePopup}
                    Title="Reativar tipo de atendimento"
                    Description={`Deseja reativar o tipo "${currentItem.Label}"? Ele volta a aparecer na escolha do tipo, com os mesmos módulos.`}
                    ConfirmLabel="Reativar"
                    OnConfirm={handleConfirmReactivate}
                    OnHide={handleReactivatePopupHide}
                />
            )}
            {isVisibleEditor && (
                <CreateEditAppointmentType
                    Data={currentItem}
                    OnUpdate={handleSaved}
                    OnHide={handleEditorClose}
                />
            )}

            <div className={styles.header}>
                <AreaTitle
                    Title="Tipos de atendimento"
                    Description="Monte cada atendimento escolhendo quais módulos aparecem na consulta e em que ordem"
                    Icon={<AppointmentTypesIcon />}
                />
                <div className={styles.headerActions}>
                    <label className={styles.showInactive}>
                        <Switch
                            size="small"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        <span>Exibir inativos</span>
                    </label>
                    <Tooltip title={!canManage ? ADD_DENIED : ''} arrow>
                        <span>
                            <CustomButton
                                style="main"
                                variant="contained"
                                icon={<AddIcon />}
                                label="Adicionar tipo"
                                onClick={handleAddType}
                                enabled={canManage}
                            />
                        </span>
                    </Tooltip>
                </div>
            </div>

            <HowItWorks />

            {loaded && types.length > 0 ? (
                <section className={styles.list}>
                    <div className={styles.listHeader}>
                        <h3 className={styles.listTitle}>Seus tipos</h3>
                        <span className={styles.listCount}>
                            {types.length} {types.length === 1 ? 'tipo' : 'tipos'}
                        </span>
                    </div>
                    <div className={styles.grid}>
                        {types.map((type) => (
                            <AppointmentTypeCard
                                key={type.Id}
                                type={type}
                                canManage={canManage}
                                onEdit={handleEditType}
                                onSetDefault={handleSetDefault}
                                onDeactivate={handleDeactivateType}
                            />
                        ))}
                        {showInactive && inactiveTypes.map((type) => (
                            <AppointmentTypeCard
                                key={type.Id}
                                type={type}
                                canManage={canManage}
                                onReactivate={handleReactivateType}
                            />
                        ))}
                    </div>
                </section>
            ) : !loaded ? (
                <div className={styles.grid}>
                    {Array.from({ length: SKELETON_CARDS }).map((_, index) => (
                        <div key={index} className={styles.skeletonCard}>
                            <div className={styles.skeletonHeader}>
                                <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: '10px' }} />
                                <div className={styles.skeletonTitle}>
                                    <Skeleton width="60%" height={20} />
                                    <Skeleton width="40%" height={14} />
                                </div>
                            </div>
                            <div className={styles.skeletonChips}>
                                {[96, 120, 84, 104].map((width) => (
                                    <Skeleton key={width} variant="rounded" width={width} height={26} sx={{ borderRadius: '999px' }} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <AppointmentTypesEmptyState onAdd={handleAddType} canManage={canManage} />
            )}
        </div>
    );
};

export default AppointmentTypes;
